'use client'
import { useEffect } from 'react'
import {
  FORM_FUNNEL_EVENTS,
  captureFormEvent,
  collectValidationErrors,
  getFieldElement,
  getFieldIndex,
  getFieldName,
  getVisibleFields,
  hasValue,
  isFormReadyMessage,
  isSubmitControl,
  isTrustedHubSpotMessageOrigin,
  type FormFunnelContext,
} from '@/utils/formFunnelEvents'

interface UseFormFunnelEventsOptions extends FormFunnelContext {
  target: string
  enabled?: boolean
}

// HubSpot validates after the click
const VALIDATION_SETTLE_MS = 150
// Enter fires submit and a synthetic click
const SUBMIT_DEDUPE_MS = 1000

/** Emits the form funnel events from container listeners, which survive HubSpot re-creating the form. */
export default function useFormFunnelEvents({
  target,
  uid,
  form_id,
  portal_id,
  form_type,
  enabled = true,
}: UseFormFunnelEventsOptions): void {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return
    const container = document.getElementById(target)
    if (!container) return

    const base: FormFunnelContext = { uid, form_id, portal_id, form_type, target }
    let loaded = false
    let started = false
    const completedFields = new Set<string>()
    let lastSubmitAt = 0
    let submitTimer: ReturnType<typeof setTimeout> | undefined

    const currentForm = () => container.querySelector('form')

    const onMessage = (event: MessageEvent) => {
      if (loaded) return
      if (!isTrustedHubSpotMessageOrigin(event.origin, window.location.origin)) return
      if (!isFormReadyMessage(event.data, form_id)) return
      loaded = true
      const form = currentForm()
      captureFormEvent(FORM_FUNNEL_EVENTS.loaded, {
        ...base,
        // Before ChiliPiper shortens the form; the settled count is on Form Revealed
        field_count_at_ready: form ? getVisibleFields(form).length : undefined,
      })
    }

    const onFocusIn = (event: Event) => {
      if (started) return
      const field = getFieldElement(event.target)
      if (!field) return
      started = true
      captureFormEvent(FORM_FUNNEL_EVENTS.started, {
        ...base,
        field_name: getFieldName(field),
        field_index: getFieldIndex(field, currentForm()),
      })
    }

    const recordCompleted = (event: Event) => {
      const field = getFieldElement(event.target)
      if (!field || !hasValue(field)) return
      const name = getFieldName(field)
      if (completedFields.has(name)) return
      completedFields.add(name)
      captureFormEvent(FORM_FUNNEL_EVENTS.fieldCompleted, {
        ...base,
        field_name: name,
        field_index: getFieldIndex(field, currentForm()),
        fields_completed: completedFields.size,
      })
    }

    const onFocusOut = (event: Event) => recordCompleted(event)

    const onChange = (event: Event) => {
      const field = getFieldElement(event.target)
      if (!field) return
      // Text fields report on blur
      const isChoice =
        field instanceof HTMLSelectElement ||
        (field instanceof HTMLInputElement && (field.type === 'checkbox' || field.type === 'radio'))
      if (isChoice) recordCompleted(event)
    }

    const scheduleSubmitAttempt = () => {
      const now = Date.now()
      if (now - lastSubmitAt < SUBMIT_DEDUPE_MS) return
      lastSubmitAt = now
      // Click time, so the attempt orders before Demo Requested
      const attemptedAt = new Date(now)
      clearTimeout(submitTimer)
      submitTimer = setTimeout(() => {
        const form = currentForm()
        const errors = form ? collectValidationErrors(form) : []
        captureFormEvent(
          FORM_FUNNEL_EVENTS.submitAttempted,
          {
            ...base,
            validation_errors: errors,
            error_count: errors.length,
            fields_completed: completedFields.size,
          },
          { timestamp: attemptedAt },
        )
      }, VALIDATION_SETTLE_MS)
    }

    const onClick = (event: Event) => {
      if (isSubmitControl(event.target)) scheduleSubmitAttempt()
    }
    const onSubmit = () => scheduleSubmitAttempt()

    window.addEventListener('message', onMessage)
    container.addEventListener('focusin', onFocusIn, true)
    container.addEventListener('focusout', onFocusOut, true)
    container.addEventListener('change', onChange, true)
    container.addEventListener('click', onClick, true)
    container.addEventListener('submit', onSubmit, true)

    return () => {
      clearTimeout(submitTimer)
      window.removeEventListener('message', onMessage)
      container.removeEventListener('focusin', onFocusIn, true)
      container.removeEventListener('focusout', onFocusOut, true)
      container.removeEventListener('change', onChange, true)
      container.removeEventListener('click', onClick, true)
      container.removeEventListener('submit', onSubmit, true)
    }
  }, [target, uid, form_id, portal_id, form_type, enabled])
}
